import { Helmet } from "react-helmet";
import styled from "styled-components";
import React, { useEffect } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { StringParam, NumberParam, withDefault, useQueryParams } from "use-query-params";
import { Col, Row, Input, Table, Empty, Button, Select, Typography, ConfigProvider } from "antd";

import Role from "components/Role";
import { TransactionNIBSS } from "components/Status";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { TransactionDTO } from "models";
import { useAppDispatch } from "app/hooks";
import type { ColumnsType } from "antd/es/table";
import { openNotificationWithIcon } from "utils";

import { updateLayout } from "pages/TPlusOneProcessing/tPlusOneProcessingSlice";
import { useGetSortCodeTypeEnumQuery, useGetTransactionTypeEnumQuery } from "app/configApiSlice";
import { useGetTransactionsQuery } from "pages/TPlusOneProcessing/tPlusOneProcessingApiSlice";

const { Search } = Input;
const { Option } = Select;

const TransactionTableContainer = styled.div`
  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 5px;
    col {
      border-radius: 4px;
    }
  }
`;

const TPlusOneProcessingBatchTransactionsTable: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ batchId: string }>();
  const dispatch = useAppDispatch();
  const transactionTypeEnum = useGetTransactionTypeEnumQuery();

  const [batchTransactionsParams, setBatchTransactionsParams] = useQueryParams({
    crDr: StringParam,
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    merchant: withDefault(StringParam, ""),
    sortCode: withDefault(StringParam, ""),
    batchId: withDefault(StringParam, params.batchId),
  });

  const sortCode = useGetSortCodeTypeEnumQuery();
  const transactions = useGetTransactionsQuery(batchTransactionsParams as any);

  const handleTableChange = (
    pagination: { current?: number; pageSize?: number },
    _filters: unknown,
    _sorter: unknown,
  ) => {
    setBatchTransactionsParams({
      size: pagination?.pageSize,
      page: (pagination?.current ?? 1) - 1,
    });
  };

  const sortCodeOptions = sortCode?.data?.response?.map
    ? sortCode?.data?.response?.map(({ code, bankName }) => ({
        value: code,
        label: bankName,
      }))
    : [];

  const handleFilterChange = (
    name: string = "",
    value: {
      value?: string;
    },
  ) => {
    setBatchTransactionsParams({ [name]: value.value });
  };

  useEffect(() => {
    dispatch(
      updateLayout({
        title: `T + 1 Processing - View Transactions - ${params.batchId}`,
        function: "VIEW",
        path: "",
      }),
    );
  }, [dispatch, params.batchId]);

  useEffect(() => {
    if (transactions.isError) {
      const error = transactions?.error as { status: number; data: { error?: string; message?: string; errorMessage?: string; errorMessageList?: string[] } };
      if (error.status >= 500 && error.status < 600) {
        openNotificationWithIcon(error.data.error, error.data.message, "error");
      } else if (error.status === 400) {
        error.data?.errorMessageList?.length
          ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
              openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
            )
          : openNotificationWithIcon(error.data.errorMessage, "", "warning");
      }
    }
  }, [transactions.isError, transactions.error]);

  const columns: ColumnsType<TransactionDTO> = [
    {
      title: "Merchant ID",
      dataIndex: "merchant",
      key: "merchant",
      width: "80px",
    },

    {
      title: "Account Name",
      dataIndex: "accountName",
      key: "accountName",
      width: "120px",
      // fixed: "left",
      render: (_: unknown, record: TransactionDTO) => (
        <Typography.Text
          style={{
            width: 280,
            fontSize: "14px",
          }}
          ellipsis={{
            tooltip: {
              color: "#f4f4f4",
              title: <Typography.Text>{record.accountName}</Typography.Text>,
            },
          }}
        >
          {record.accountName}
        </Typography.Text>
      ),
    },
    {
      title: "Account Number",
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: "130px",
      align: "center",
    },

    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "50px",
      align: "right",
    },

    {
      title: "CR-DR",
      dataIndex: "crDr",
      key: "beneficiaryBVN",
      width: "80px",
      align: "center",
      render: (value) => <Role role={value} />,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      align: "center",
      width: "70px",
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "100px",
      fixed: "right",
      align: "center",
      render: (value) => <TransactionNIBSS status={value} />,
    },
  ];

  return (
    <TransactionTableContainer>
      <Helmet>
        <title>NEFT Settlement | Transactions</title>
        <meta name="description" content="Helmet application" />
      </Helmet>
      <Row justify="space-between" style={{ padding: "15px 0px" }}>
        <Col>
          <Row gutter={10}>
            <Col>
              <Button
                size="large"
                shape="circle"
                onClick={() => navigate(-1)}
                icon={<ArrowLeftOutlined />}
              />
            </Col>

            <Col>
              <Select
                size="large"
                suffixIcon="CR-DR"
                labelInValue={true}
                defaultValue={{ value: "" }}
                onChange={(value: {
                  key?: string;
                  label?: string;
                  value?: string;
                  disabled?: boolean;
                }) => handleFilterChange("crDr", value)}
                style={{ width: 140 }}
              >
                <Option value="">All</Option>
                {transactionTypeEnum?.data &&
                  transactionTypeEnum?.data?.response?.map(({ code, description }) => (
                    <Option value={code}>{description}</Option>
                  ))}
              </Select>
            </Col>

            <Col>
              <Select
                showSearch
                size="large"
                labelInValue={true}
                suffixIcon="SORTCODE"
                style={{ width: 250 }}
                optionFilterProp="children"
                defaultValue={{ value: "" }}
                options={[{ value: "", label: " All", key: "all" }, ...sortCodeOptions]}
                filterOption={(input, option) => {
                  return (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase());
                }}
                filterSort={(optionA, optionB) => {
                  return (optionA?.label?.toLowerCase() ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label?.toLowerCase() ?? "").toLowerCase());
                }}
                onChange={(value: {
                  key?: string;
                  label?: string;
                  value?: string;
                  disabled?: boolean;
                }) => handleFilterChange("sortCode", value)}
              />
            </Col>
          </Row>
        </Col>

        <Col>
          <Search
            allowClear
            enterButton
            size="large"
            defaultValue=""
            style={{ width: "auto" }}
            addonBefore="MerchantID"
            placeholder="Enter Merchant ID"
            onSearch={(value: string) => handleFilterChange("merchant", { value })}
          />
        </Col>
      </Row>

      <ConfigProvider
        renderEmpty={() =>
          transactions.isError ? (
            <ApiErrorTableComponent queryResult={transactions} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
      >
        <Table
          key="uuid"
          columns={columns}
          scroll={{ x: "auto" }}
          rowKey={(record: TransactionDTO) => record?.batchID || ""}
          dataSource={transactions.isError ? [] : transactions.data?.response.content}
          pagination={{
            showTitle: true,
            showSizeChanger: true,
            showQuickJumper: true,
            showPrevNextJumpers: true,
            pageSizeOptions: [5, 10, 20, 50, 100, 200],
            pageSize: transactions.data?.response.size,
            total: transactions.data?.response.totalElements,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={transactions.isFetching}
          onChange={handleTableChange}
        />
      </ConfigProvider>
    </TransactionTableContainer>
  );
};

export default TPlusOneProcessingBatchTransactionsTable;
